import React from 'react'
import { Link } from 'react-router-dom'
import { Table, Space, Button, Tag, List, Popover, Tooltip, Typography } from 'antd'
import { EditOutlined, PlusOutlined, QrcodeOutlined } from '@ant-design/icons'
import { ListComponent, BaseListComponentProps } from '../BaseComponent'
import IBranch from '../../interfaces/IBranch'
import IKiosk from '../../interfaces/IKiosk'

type KioskBranchListProps = BaseListComponentProps & {
  branches: any,
  loading: boolean,

  onKioskShow: (kiosk: IKiosk) => void
  onKioskCreate: (branch: IBranch) => void
}

export default class KioskBranchList extends ListComponent<KioskBranchListProps> {

  static defaultProps = {
    loading: false
  }

  tableColumns = [
    {
      title: 'UUID',
      dataIndex: 'uuid',
      key: 'uuid',
      responsive: ['md'] as any,
      sorter: (a: any, b: any) => a.uuid - b.uuid
    },
    {
      title: 'Location',
      dataIndex: 'name',
      key: 'name',
      defaultSortOrder: 'ascend',
      sorter: (a: any, b: any) => a.name - b.name
    },
    {
      title: 'Has Settings',
      dataIndex: 'active',
      key: 'active',
      responsive: ['md'] as any,
      sorter: (a: any, b: any) => a.kiosk_reasons?.length - b.kiosk_reasons?.length,
      render: (text, record: IBranch) => (
        <Tag color={record.kiosk_reasons?.length ? 'green' : 'purple'}>{record.kiosk_reasons?.length ? 'Yes' : 'No'}</Tag>
      )
    },

    {
      title: 'Kiosks',
      dataIndex: 'kiosks',
      key: 'kiosks',
      responsive: ['md'] as any,
      render: (text, record: IBranch) =>  {
        // Filter only TOTEM
        const kiosks = record.kiosks?.filter(v => v.type_key === 'TOTEM') || []

        const kioskHeader = (
          <div style={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between'}}>
            <Typography.Title level={5}>
              Kiosks
            </Typography.Title>
          </div>
        )

        const popoverContent = (
          <div style={{height: 200, overflow: 'auto'}}>
            <List
              bordered={true}
              dataSource={kiosks}
              style={{minWidth: 300}}
              header={kioskHeader}
              renderItem={(item: IKiosk) => {
                return (
                  <List.Item>
                    <List.Item.Meta 
                      title={item.name || 'Unnamed'}
                      description={item.type_key}
                    />
                  </List.Item>
                )
              }}
            />
          </div>
        )

        return (
          <Popover content={popoverContent} title={null} trigger="hover" placement="bottom">
            <Tag color="geekblue">{kiosks.length}</Tag>
          </Popover>
        )
      }
    },

    {
      title: 'Self Check-in Kiosk',
      dataIndex: 'kiosks_self_checkin',
      key: 'kiosks_self_checkin',
      responsive: ['md'] as any,
      render: (text, record: IBranch) =>  {
        // Filter only SELF CHECK IN
        const kiosks = record.kiosks?.filter(v => v.type_key === 'SELF_CHECK_IN') || []

        const kioskAddButton = (
          <div style={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between'}}>
            <Typography.Title level={5}>
              Self Check-in Kiosk
            </Typography.Title>

            <Button
              type="primary"
              shape="round"
              size="small"
              icon={<PlusOutlined />}
              onClick={() => this.props.onKioskCreate(record)}
            >
              Add
            </Button>
          </div>
        )

        const popoverContent = (
          <div style={{height: 200, overflow: 'auto'}}>
            <List
              bordered={true}
              dataSource={kiosks}
              style={{minWidth: 300}}
              header={kioskAddButton}
              renderItem={(item: IKiosk) => {
                const showQrCode = (
                  <Tooltip placement="left" title="Get QR Code">
                    <Button type="link" onClick={() => this.props.onKioskShow(item)}>
                      <QrcodeOutlined style={{fontSize: 32}} />
                    </Button>
                  </Tooltip>
                )

                return (
                  <List.Item
                    actions={[showQrCode]}
                  >
                    <List.Item.Meta 
                      title={item.name || 'Unnamed'}
                      description={item.type_key}
                    />
                  </List.Item>
                )
              }}
            />
          </div>
        )

        return (
          <Popover content={popoverContent} title={null} trigger="hover" placement="bottom">
            <Tag color="geekblue">{kiosks.length}</Tag>
          </Popover>
        )
      }
    },
    {
      title: 'Actions',
      key: 'action',
      align: 'right',
      render: (text: string, record: any) => (
        <Space size="middle">
          <Link to={`kiosk/${record.uuid}`} ref={(refs) => this.actionRefs.edit.push(refs)}>
            <Button type="dashed" shape="round" icon={<EditOutlined />} />
          </Link>
        </Space>
      )
    }
  ] as any

  render() {
    const { loading, branches } = this.props

    return (
      <Table
        loading={loading}
        rowKey='uuid'
        columns={this.tableColumns}
        dataSource={branches}
      />
    )
  }
}
